
import Page from "../molecules/Page.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import ActionButton from "../atoms/ActionButton.vue";
import UserSelector from "../molecules/UserSelector.vue";
import ProgressChart from "../molecules/ProgressChart.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import { Options } from "vue-class-component";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import FieldLabel from "../atoms/FieldLabel.vue";
import Multiselect from "@vueform/multiselect";
import { SelectableProfile, GetProfile, UserProgressStats, GetExamDate, GetStat } from "../../types";
import { StatsItemEnum } from "../../autogen";
import { print } from "../../helpers/view-utils";
import { GraphColors } from "../../helpers/const";

export type ChartData = {
  userId: string;
  userName: string;
  examDate: string[];
  examDateOptions: Object;
  datas: GraphData;
};
export type GraphData = {
  labels: string[] | string[][];
  datasets: DataSet[];
};

type DataSet = {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  data: Object[];
  label: string;
  examDate?: string;
};

@Options({
  components: {
    Page,
    HelpIcon,
    ActionButton,
    ProgressChart,
    UserSelector,
    DepartmentUserSelector,
    FieldLabel,
    Multiselect
  }
})
export default class Progress extends ChartViewMixin {
  participantsMin = 1;
  participantsMax = 100;
  focusedUser: Partial<UserProgressStats> = {};
  selectedUsers: SelectableProfile[] = [];
  stats: (GetExamDate & GetStat & GetProfile)[] = [];
  chartDatas: ChartData[] = []; // 元データ。このデータは取得後は変更しない
  displayChartDatas: ChartData[] = []; // chartDatasから生成してセットする表示用のデータ
  scoreDiffs: number[][] = [];
  chartOptions = {
    interaction: {
      intersect: false,
      mode: "index"
    },
    scales: {
      y: {
        suggestedMin: 10,
        suggestedMax: 90
      }
    },
    plugins: {
      tooltip: {
        intersect: false,
        callbacks: {
          title: (tooltipItems: any) => {
            const title = tooltipItems[0].label.replaceAll(",", "").replaceAll("丨", "ー");
            return title;
          }
        }
      }
    }
  };
  activePath = "main";
  peopleId = "";
  onViewButtonClick() {
    this.activePath = "main";
  }
  onTutorialButtonClick() {
    this.activePath = "sub";
  }
  async userSelect() {
    this.targetStats = [StatsItemEnum.ExamDate, StatsItemEnum.Progress];
    this.onSelectionDoneButtonClick();
  }
  async onSelectionDone() {
    this.changeFocus(this.selectedUsers[0]);
    (this.$refs["selector"] as UserSelector).onUserClick(this.selectedUsers[0]);
    if (this.selectedUsers.length === 0) return;

    this.stats = this.selectedUsers.map(selectedUser =>
      Object.assign(
        this.chartData![selectedUser.id].profile,
        this.chartData![selectedUser.id].stats["ExamDate"],
        { Progress: this.chartData![selectedUser.id].stats["Progress"] },
        {
          id: selectedUser.id
        }
      )
    );
    if (!this.stats) return;
    this.chartDatas = this.stats.map(stat => {
      if (stat.examDates && stat.Progress) {
        const examDates = stat.examDates.map(date => this.convertDateFormat(date));
        const laterDates = stat.examDates
          .sort((a, b) => (a > b ? -1 : 1))
          .slice(0, 3)
          .map(date => this.convertDateFormat(date));

        return {
          userId: stat.id,
          userName: stat.lastName + stat.firstName,
          examDate: laterDates,
          examDateOptions: {
            mode: "tags",
            value: [],
            label: "label",
            trackBy: "value",
            closeOnSelect: false,
            options: examDates.sort((a, b) => (a > b ? -1 : 1)).map(date => ({ value: date, label: `${date}時` })),
            reachable: true,
            createOption: false,
            delay: 0
          },
          datas: {
            labels: stat.Progress[0].score.map(score =>
              score.name
                .replace(/ー/g, "丨")
                .replace("ﾓﾗﾄﾘｱﾑ", "モラトリアム")
                .split("")
            ),
            datasets: stat.Progress.sort((a, b) => (a.date > b.date ? -1 : 1))
              .filter(progress => this.convertDateFormat(progress.date))
              .map((progress, index) => ({
                label: `${this.convertDateFormat(progress.date)}時`,
                data: progress.score.map(score => Math.round(score.value)),
                backgroundColor: GraphColors[index],
                borderColor: GraphColors[index],
                borderWidth: 2,
                examDate: progress.date
              }))
          }
        };
      }
    }) as ChartData[];
    // 元データを表示用の変数にコピー
    this.displayChartDatas = JSON.parse(JSON.stringify(this.chartDatas));
    this.displayChartDatas = this.displayChartDatas.map(data => {
      if (data.datas) {
        return {
          userId: data.userId,
          userName: data.userName,
          examDate: data.examDate,
          examDateOptions: data.examDateOptions,
          datas: {
            labels: data.datas.labels,
            datasets: data.datas.datasets.filter(dataset =>
              data.examDate.map(d => d.replace(/\/|\s+/g, "")).includes(dataset.examDate!)
            )
          }
        };
      }
    }) as ChartData[];
    // 日付表示のために先頭の経年データを取得
    const displayChartData = await this.displayChartDatas.find(d => d.userId === this.focusedUser.id);
    if (!displayChartData) return;
    this.onChangeExamDate(displayChartData.examDate);
  }
  async switchUser(selectedUser: SelectableProfile) {
    this.changeFocus(selectedUser);
  }
  async changeFocus(selectedUser: SelectableProfile) {
    this.focusedUser = selectedUser;
  }
  onChangeExamDate(examDates: string[]) {
    const currentChartData = this.displayChartDatas.find(d => d.userId === this.focusedUser.id);
    // データを抜き出すときは元データから
    const storedChartData = this.chartDatas.find(d => d.userId === this.focusedUser.id);
    if (currentChartData && storedChartData) {
      // 元データから切り替えた日付でフィルタリングしてデータをセットする
      currentChartData.datas.datasets = storedChartData.datas.datasets
        .filter(d => (examDates ? examDates.map(date => date.replace(/\/|\s+/g, "")).includes(d.examDate!) : false))
        .map((dataset, index) => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: GraphColors[index],
          borderColor: GraphColors[index],
          borderWidth: 2,
          examDate: dataset.examDate
        }));
    }

    (this.$refs[`chart_${this.focusedUser.id}`] as ProgressChart).rerenderChart();
  }
  onPrintButtonClick() {
    const printTarget = this.displayChartDatas.find(data => data.userId === this.focusedUser.id);
    if (printTarget) {
      print(StatsItemEnum.Progress, printTarget, this.chartOptions);
    }
  }
  /**
   * 文字列を日付のフォーマットに変換する
   * @param str yyyymmddhh の文字列
   * @return yyyy/mm/dd hh のフォーマットに変換した文字列
   */
  convertDateFormat(str: string) {
    return `${str.substr(0, 4)}/${str.substr(4, 2)}/${str.substr(6, 2)} ${str.substr(8, 2)}`;
  }
}
